import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Pre-fills a public form's identity fields (name / email / company) from the signed-in
 * user, so members don't retype what we already know. Signed-out visitors are unaffected.
 *
 * Deliberately non-destructive:
 *   - runs once per mount (ref guard), because the auth session resolves asynchronously
 *     and re-filling on every change would fight the user as they type;
 *   - only writes to fields that are still empty, so anything already entered survives;
 *   - `company` is filled only when the form actually has that field.
 * Every field stays fully editable afterwards - this is a starting value, not a lock.
 *
 * Usage: pass the form's setState function.  useIdentityPrefill(setForm);
 */
export function useIdentityPrefill(setForm) {
  const { user } = useAuth();
  const filled = useRef(false);

  useEffect(() => {
    if (filled.current || !user) return;
    filled.current = true;

    const meta = user.user_metadata || {};
    const name = (meta.full_name || [meta.first_name, meta.last_name].filter(Boolean).join(' ')).trim();

    setForm((f) => ({
      ...f,
      name: f.name || name,
      email: f.email || user.email || '',
      ...('company' in f ? { company: f.company || meta.company || '' } : {}),
    }));
  }, [user, setForm]);
}
